import {
  daysInMonth,
  gregorianToHijri,
  gregorianToJalali,
  hijriToGregorian,
  jalaliToGregorian,
  type CalendarSystem,
  type CivilDate,
  type Lang,
} from "@calendar/shared";

/**
 * Iranian public holidays and commemorations.
 *
 * The rules are written the way the official calendar is published, but the
 * Islamic observances are lunar: they drift about eleven days earlier every
 * solar year and so cannot be tabulated as fixed dates.
 *
 * The resolution is one pivot. Every rule is resolved to an absolute day, and
 * that day is then expressed in whichever calendar the user is looking at. No
 * hand-written month arithmetic, and the lunar entries land correctly for any
 * year.
 */
export interface Occasion {
  /** Already localised, ready to display. */
  readonly name: string;
  /** A public holiday closes the day; an occasion only marks it. */
  readonly holiday: boolean;
}

interface Rule {
  readonly fa: string;
  readonly en: string;
  readonly holiday: boolean;
  /** Set for rules fixed to a Jalali date. */
  readonly jalali?: { month: number; day: number };
  /**
   * Set for the last day of a Jalali month. Esfand is 29 or 30 days, so "the
   * last day of the year" cannot be written as a fixed day.
   */
  readonly endOfJalaliMonth?: number;
  /** Set for rules given as a Hijri date, which move every solar year. */
  readonly hijri?: { month: number; day: number };
}

/** Fixed solar dates, in the order the government lists them. */
const SOLAR: Rule[] = [
  { jalali: { month: 1, day: 1 }, fa: "عید نوروز", en: "Nowruz", holiday: true },
  { jalali: { month: 1, day: 2 }, fa: "عید نوروز", en: "Nowruz", holiday: true },
  { jalali: { month: 1, day: 3 }, fa: "عید نوروز", en: "Nowruz", holiday: true },
  { jalali: { month: 1, day: 4 }, fa: "عید نوروز", en: "Nowruz", holiday: true },
  { jalali: { month: 3, day: 14 }, fa: "تولد امام خمینی", en: "Imam Khomeini's Birth", holiday: true },
  { jalali: { month: 3, day: 15 }, fa: "قیام ۱۵ خرداد", en: "15 Khordad Uprising", holiday: true },
  { jalali: { month: 11, day: 22 }, fa: "پیروزی انقلاب اسلامی", en: "Islamic Revolution Victory", holiday: false },
  { jalali: { month: 12, day: 29 }, fa: "روز ملی شدن صنعت نفت", en: "Oil Nationalisation Day", holiday: false },
  {
    endOfJalaliMonth: 12,
    fa: "آخرین روز سال",
    en: "Last Day of the Year",
    holiday: true,
  },
];

/**
 * Observances that follow the lunar calendar, given as Hijri month and day.
 *
 * The official Iranian calendar is published each year and can differ from a
 * plain astronomical conversion by a day, so these are the conventional dates.
 * They are the right default for marking a day, not a substitute for the
 * gazetted schedule when the exact closure date matters.
 */
const LUNAR: Rule[] = [
  { hijri: { month: 1, day: 9 }, fa: "تاسوعای حسینی", en: "Tasu'a", holiday: false },
  { hijri: { month: 1, day: 10 }, fa: "عاشورای حسینی", en: "Ashura", holiday: true },
  { hijri: { month: 2, day: 30 }, fa: "شهادت امام رضا (ع)", en: "Imam Reza's Departure", holiday: false },
  { hijri: { month: 3, day: 12 }, fa: "میلاد پیامبر اکرم (ص)", en: "Prophet's Birthday", holiday: true },
  { hijri: { month: 7, day: 13 }, fa: "تولد امام علی (ع)", en: "Imam Ali's Birth", holiday: false },
  { hijri: { month: 8, day: 30 }, fa: "تولد امام جواد (ع)", en: "Imam Jawad's Birth", holiday: false },
  { hijri: { month: 9, day: 1 }, fa: "عید سعید فطر", en: "Eid al-Fitr", holiday: true },
  { hijri: { month: 12, day: 10 }, fa: "عید سعید قربان", en: "Eid al-Adha", holiday: true },
  { hijri: { month: 12, day: 18 }, fa: "عید غدیر خم", en: "Eid al-Ghadir", holiday: true },
];

export type OccasionMap = Map<string, Occasion>;

export function dayKey(day: CivilDate): string {
  return `${day.year}-${day.month}-${day.day}`;
}

/** Jalali to Gregorian, returning null instead of throwing on a bad input. */
function toGregorian(jalali: CivilDate): CivilDate | null {
  try {
    return jalaliToGregorian(jalali);
  } catch {
    return null;
  }
}

/** Gregorian to Jalali, returning null instead of throwing. */
function toJalali(gregorian: CivilDate): CivilDate | null {
  try {
    return gregorianToJalali(gregorian);
  } catch {
    return null;
  }
}

/** Gregorian to Hijri, returning null instead of throwing. */
function toHijri(gregorian: CivilDate): CivilDate | null {
  try {
    return gregorianToHijri(gregorian);
  } catch {
    return null;
  }
}

/** Express a day in the calendar on screen, as a key the views can look up. */
function keyIn(gregorian: CivilDate, calendar: CalendarSystem): string | null {
  if (calendar === "gregorian") return dayKey(gregorian);
  if (calendar === "jalali") {
    const j = toJalali(gregorian);
    return j ? dayKey(j) : null;
  }
  const h = toHijri(gregorian);
  return h ? dayKey(h) : null;
}

/**
 * The Hijri years that overlap a given Jalali year.
 *
 * A Jalali year runs from late March to late March, so it straddles two Hijri
 * years: the one that began in its spring and the one that begins in its last
 * month. 21 March 2026 is both 1 Farvardin 1405 and 2 Shawwal 1447, so the
 * offset is 42, and an observance late in a Jalali year belongs to the
 * *following* Hijri year.
 *
 * All three candidates are tried and each is recorded only if the resulting
 * Gregorian day really falls inside the Jalali year asked for. That handles the
 * boundary years without a table of exceptions, and the converter's
 * out-of-range assertion is caught rather than thrown.
 */
function hijriYearsFor(jalaliYear: number): number[] {
  return [jalaliYear + 42, jalaliYear + 41, jalaliYear + 43];
}

/**
 * Every occasion falling inside the given Jalali year, keyed by the day it
 * lands on in the calendar the user is viewing.
 *
 * Only days genuinely inside the year are recorded, so a lunar observance that
 * drifts into a neighbouring year is not shown in the wrong one.
 */
export function buildYearOccasions(
  jalaliYear: number,
  calendar: CalendarSystem,
  lang: Lang,
): OccasionMap {
  const map: OccasionMap = new Map();

  /**
   * Record a day, keeping the more important of two rules that land on it.
   *
   * Order matters: in a leap year Esfand has a 30th, and both Oil
   * Nationalisation Day and the last day of the year fall on it. The holiday
   * wins, because the user needs to know the office is closed, not that there
   * happens to be an anniversary.
   */
  const rank = (holiday: boolean) => (holiday ? 2 : 1);

  const record = (gregorian: CivilDate, rule: Rule) => {
    const jalali = toJalali(gregorian);
    if (!jalali || jalali.year !== jalaliYear) return;
    const key = keyIn(gregorian, calendar);
    if (!key) return;
    const existing = map.get(key);
    if (existing && rank(existing.holiday) >= rank(rule.holiday)) return;
    map.set(key, {
      name: lang === "fa" ? rule.fa : rule.en,
      holiday: rule.holiday,
    });
  };

  for (const rule of SOLAR) {
    let jalali: CivilDate | null = null;
    if (rule.jalali) {
      jalali = { year: jalaliYear, ...rule.jalali };
    } else if (rule.endOfJalaliMonth) {
      try {
        jalali = {
          year: jalaliYear,
          month: rule.endOfJalaliMonth,
          day: daysInMonth({ year: jalaliYear, month: rule.endOfJalaliMonth }, "jalali"),
        };
      } catch {
        // A year outside the converter's supported range. Nothing to record.
      }
    }
    if (!jalali) continue;
    const gregorian = toGregorian(jalali);
    if (gregorian) record(gregorian, rule);
  }

  for (const rule of LUNAR) {
    if (!rule.hijri) continue;
    for (const hijriYear of hijriYearsFor(jalaliYear)) {
      let gregorian: CivilDate;
      try {
        gregorian = hijriToGregorian({ year: hijriYear, ...rule.hijri });
      } catch {
        // Either a 30th of a 29-day Hijri month, or a year outside the
        // converter's supported range. Either way this is not a real date.
        continue;
      }
      record(gregorian, rule);
    }
  }

  return map;
}
